/**
 * flora_scatter.ts — the main-thread half of the geology + understory scatter (2026-09-25, visual-realism R3/R6).
 *
 * The worker (workers/flora_scatter_worker.ts) plants a chunk and replies with PACKED arrays; this side
 * only FILTERS what the worker cannot see (baked lakes, placed structures, settlement plan roads and pads)
 * and UPLOADS:
 *   · rocks, logs, stumps, pebbles → ONE shared StaticInstancer ('geology': shadows cast + received, a roof
 *     for the rain-shelter bake, per-instance jitter) — batches span chunks, one draw per (mesh, material);
 *   · plants → per chunk, per species, ONE InstancedMesh whose instance matrices ARE the worker's array
 *     (chunk-centre-relative float32, the mesh seated at the chunk centre — the scene rebase keeps it exact),
 *     wearing the species' shared wind material, per-instance hue/value from `instanceColor`;
 *   · colliders: rocks and logs flagged by the worker get a TRIMESH (the low-detail twin with the full
 *     instance rotation and scale BAKED in — the physics pose is yaw-only) while within 40 m of the player,
 *     released past 52 m (the trunk-collider pattern).
 */
import * as THREE from 'three/webgpu';
import { StaticInstancer, jitterRgb } from '../instancing.js';
import { disposeLater } from '../gpu_dispose.js';
import { rockGeometry, rockColliderMesh, ROCK_KINDS, type RockKind } from './rock_mesh.js';
import { rockMaterial } from './rock_material.js';
import { deadwoodGeometry, deadwoodColliderMesh, barkMaterial } from './deadwood.js';
import { BIOMES } from '@shared/terrain.js';
import type { ColliderProxy } from '../collider_proxy.js';
import type { ScatterJob, ScatterResult } from '../../workers/flora_scatter_worker.js';
import { RAIN_SHELTER_LAYER } from '../rain_shelter.js';

export const GEOLOGY_COLLIDE_M = 40;
/** Understory triangle budgets per chunk (visual-realism R6): one species, and all of them. */
export const UNDERSTORY_SPECIES_TRIS = 40_000;
export const UNDERSTORY_CHUNK_TRIS = 150_000;
const DROP_M = GEOLOGY_COLLIDE_M + 12;

export interface FloraScatterHost {
  scene: THREE.Scene;
  keyToAsset(): Map<string, bigint>;
  loadRep(assetId: bigint, lod: number): Promise<{ object: THREE.Object3D; release: () => void }>;
  /** The species' shared material (wind, cloud shadows) for a loaded mesh. */
  speciesMaterial(assetId: bigint, lod: number, geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Material;
  /** Keep-out filters the worker cannot evaluate. true = the point is blocked. */
  blocked(x: number, z: number, r: number, wet: boolean): boolean;
  physics: { addStaticCollider(id: string, proxy: ColliderProxy, t: { x: number; y: number; z: number; yaw: number; scale: number }): boolean; removeStaticCollider(id: string): void } | null;
  warm?(o: THREE.Object3D): void;
  onError?(msg: string): void;
}
interface ColliderRec { id: string; kind: number; variant: number; x: number; y: number; z: number; q: [number, number, number, number]; s: [number, number, number] }
interface ChunkRec { rocks: number[]; plants: THREE.InstancedMesh[]; releases: Array<() => void>; colliders: ColliderRec[]; counts: Record<string, number>; ms: number }

export class FloraScatter {
  private worker: Worker | null = null;
  private broken = false;
  private seq = 0;
  private readonly waiting = new Map<number, (r: ScatterResult | null) => void>();
  private rocks: StaticInstancer | null = null;
  private readonly chunks = new Map<string, ChunkRec>();
  private readonly live = new Set<string>(); // collider ids in the physics world
  private lastColliderMs = 0;
  readonly stats = { jobs: 0, results: 0, dropped: 0, rocks: 0, plants: 0, colliders: 0, workerMs: 0, filtered: 0, capped: 0 };

  constructor(private readonly host: FloraScatterHost) {}

  private ensureWorker(): Worker | null {
    if (this.worker || this.broken) return this.worker;
    try {
      this.worker = new Worker(new URL('../../workers/flora_scatter_worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<ScatterResult>) => { const cb = this.waiting.get(e.data.id); this.waiting.delete(e.data.id); cb?.(e.data); };
      this.worker.onerror = (e) => { this.host.onError?.(`flora scatter worker failed: ${e.message}`); this.broken = true; for (const cb of this.waiting.values()) cb(null); this.waiting.clear(); this.worker?.terminate(); this.worker = null; };
    } catch { this.broken = true; }
    return this.worker;
  }

  /** Scatter one chunk (resolves when uploaded, or dropped because `current()` went false meanwhile). */
  async scatter(job: Omit<ScatterJob, 'id'> & { plantLod?: number }, current: () => boolean, blockedFn?: (x: number, z: number, r: number, wet: boolean) => boolean): Promise<void> {
    const blocked = blockedFn ?? this.host.blocked;
    const w = this.ensureWorker(); if (!w) return;
    const id = ++this.seq; this.stats.jobs++;
    const res = await new Promise<ScatterResult | null>((resolve) => { this.waiting.set(id, resolve); w.postMessage({ ...job, id }); });
    if (!res) return;
    this.stats.results++; this.stats.workerMs = res.ms;
    if (!current()) { this.stats.dropped++; return; }
    this.release(job.key); // a re-scatter replaces the chunk's previous set
    const rec: ChunkRec = { rocks: [], plants: [], releases: [], colliders: [], counts: res.counts, ms: res.ms };
    this.chunks.set(job.key, rec);
    // ── rocks, logs, stumps, pebbles ──
    if (!this.rocks) {
      this.rocks = new StaticInstancer(this.host.scene, 4096, 'geology');
      this.rocks.setShadows(true, true);
      this.rocks.setRainLayer(true);
    }
    const m = new THREE.Matrix4(), p = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3();
    const R = res.rocks;
    for (let o = 0; o + 15 <= R.length; o += 15) {
      const kind = R[o], variant = R[o + 1], biome = BIOMES[R[o + 2]] ?? 'rocky_highlands';
      const x = R[o + 3], y = R[o + 4], z = R[o + 5], r = R[o + 13];
      if (blocked(x, z, r, false)) { this.stats.filtered++; continue; }
      p.set(x, y, z); q.set(R[o + 6], R[o + 7], R[o + 8], R[o + 9]); s.set(R[o + 10], R[o + 11], R[o + 12]);
      m.compose(p, q, s);
      const geo = kind <= 4 ? rockGeometry(ROCK_KINDS[kind] as RockKind, variant) : deadwoodGeometry(kind === 5 ? 'log' : 'stump', variant);
      const mat = kind <= 4 ? rockMaterial(biome) : barkMaterial(kind === 5 ? 'log' : 'stump');
      const hid = this.rocks.add(geo, mat, m);
      if (hid >= 0) { rec.rocks.push(hid); this.stats.rocks++; }
      if (R[o + 14] > 0.5) rec.colliders.push({ id: `geo:${job.key}:${o / 15}`, kind, variant, x, y, z, q: [R[o + 6], R[o + 7], R[o + 8], R[o + 9]], s: [R[o + 10], R[o + 11], R[o + 12]] });
    }
    // ── plants: one InstancedMesh per species per chunk ──
    const k2a = this.host.keyToAsset();
    let chunkTris = 0;
    const ccx = (job.cx + 0.5) * job.size, ccz = (job.cz + 0.5) * job.size;
    for (let i = 0; i < res.plantKeys.length; i++) {
      const assetId = k2a.get(res.plantKeys[i]); if (assetId === undefined) continue;
      const mats = res.plantMats[i], xz = res.plantXZ[i];
      // filter in place: keep rows the main-thread keep-outs allow
      let n = 0; const total = xz.length / 2;
      const wet = res.plantKeys[i].includes('reed');
      for (let k = 0; k < total; k++) {
        if (blocked(xz[k * 2], xz[k * 2 + 1], 0.4, wet)) { this.stats.filtered++; continue; }
        if (n !== k) mats.copyWithin(n * 16, k * 16, k * 16 + 16);
        xz[n * 2] = xz[k * 2]; xz[n * 2 + 1] = xz[k * 2 + 1]; n++;
      }
      if (n === 0) continue;
      let rep: { object: THREE.Object3D; release: () => void };
      try { rep = await this.host.loadRep(assetId, job.plantLod ?? 0); } catch { continue; }
      if (!current() || this.chunks.get(job.key) !== rec) { rep.release(); return; }
      let found: THREE.Mesh | null = null;
      rep.object.traverse((o) => { if (!found && (o as THREE.Mesh).isMesh) found = o as THREE.Mesh; });
      const src = found as THREE.Mesh | null;
      if (!src) { rep.release(); continue; }
      // THE TRIANGLE BUDGET (the whale law): a species may spend UNDERSTORY_SPECIES_TRIS a chunk and the chunk
      // UNDERSTORY_CHUNK_TRIS in all — a re-cooked card is 8 triangles (thousands fit), a 1.5 k succulent a few
      // dozen, and an un-re-cooked 20 k tuft two (the registry decides; this never lets the old assets flood a frame).
      const tris = src.geometry.index ? src.geometry.index.count / 3 : src.geometry.getAttribute('position').count / 3;
      const cap = Math.max(0, Math.min(Math.floor(UNDERSTORY_SPECIES_TRIS / Math.max(1, tris)), Math.floor((UNDERSTORY_CHUNK_TRIS - chunkTris) / Math.max(1, tris))));
      if (cap < n) { // thin EVENLY over the chunk (the rows are in lattice order — a prefix would keep one strip)
        this.stats.capped += n - cap;
        for (let j = 0; j < cap; j++) { const k = Math.floor((j * n) / cap); if (k !== j) { mats.copyWithin(j * 16, k * 16, k * 16 + 16); xz[j * 2] = xz[k * 2]; xz[j * 2 + 1] = xz[k * 2 + 1]; } }
        n = cap;
      }
      if (n <= 0) { rep.release(); continue; }
      chunkTris += n * tris;
      const mat = this.host.speciesMaterial(assetId, job.plantLod ?? 0, src.geometry, Array.isArray(src.material) ? src.material[0] : src.material);
      const im = new THREE.InstancedMesh(src.geometry, mat, n);
      im.instanceMatrix = new THREE.InstancedBufferAttribute(mats.slice(0, n * 16), 16);
      im.count = n;
      // R9: per-instance hue / value jitter from the world position (three multiplies diffuse by instanceColor)
      const col = new Float32Array(n * 3);
      for (let k = 0; k < n; k++) { const [r, g, b] = jitterRgb(xz[k * 2], xz[k * 2 + 1]); col[k * 3] = r; col[k * 3 + 1] = g; col[k * 3 + 2] = b; }
      im.instanceColor = new THREE.InstancedBufferAttribute(col, 3);
      im.position.set(ccx, 0, ccz);
      im.matrixAutoUpdate = false; im.updateMatrix();
      im.computeBoundingSphere();
      im.castShadow = false; // the groundcover rule: sub-pixel casters by the thousand
      im.receiveShadow = true;
      im.layers.disable(RAIN_SHELTER_LAYER);
      im.name = `understory:${res.plantKeys[i].replace('foliage:', '')}`;
      this.host.scene.add(im);
      this.host.warm?.(im);
      rec.plants.push(im); rec.releases.push(rep.release);
      this.stats.plants += n;
    }
  }

  /** Drop a chunk's set (streamed out / re-scattered). Deferred GPU disposal — the frame in flight may draw it. */
  release(key: string): void {
    const rec = this.chunks.get(key); if (!rec) return;
    this.chunks.delete(key);
    for (const h of rec.rocks) this.rocks?.remove(h);
    this.stats.rocks -= rec.rocks.length;
    for (const im of rec.plants) { this.host.scene.remove(im); this.stats.plants -= im.count; disposeLater(im); }
    for (const r of rec.releases) r();
    for (const c of rec.colliders) if (this.live.has(c.id)) { this.host.physics?.removeStaticCollider(c.id); this.live.delete(c.id); }
  }

  /** Colliders for the rocks and logs within 40 m (every 500 ms; released past 52 m). */
  colliderTick(px: number, pz: number): void {
    const phys = this.host.physics; if (!phys) return;
    const now = performance.now(); if (now - this.lastColliderMs < 500) return; this.lastColliderMs = now;
    const want = new Set<string>();
    for (const rec of this.chunks.values()) for (const c of rec.colliders) {
      const d2 = (c.x - px) ** 2 + (c.z - pz) ** 2;
      if (d2 > GEOLOGY_COLLIDE_M * GEOLOGY_COLLIDE_M) { if (this.live.has(c.id) && d2 > DROP_M * DROP_M) { phys.removeStaticCollider(c.id); this.live.delete(c.id); } else if (this.live.has(c.id)) want.add(c.id); continue; }
      want.add(c.id);
      if (this.live.has(c.id)) continue;
      if (phys.addStaticCollider(c.id, bakedProxy(c), { x: c.x, y: c.y, z: c.z, yaw: 0, scale: 1 })) this.live.add(c.id);
    }
    this.stats.colliders = this.live.size;
  }
  /** Probe: every live collider's placement (id → world position). */
  colliderIds(): string[] { return [...this.live]; }
  /** Probe: the world bases of the rocks around a point (x, y, z of each piece's origin + its kind). */
  piecesNear(x: number, z: number, r: number): Array<{ kind: string; x: number; y: number; z: number; collider: boolean }> {
    const out: Array<{ kind: string; x: number; y: number; z: number; collider: boolean }> = [];
    for (const rec of this.chunks.values()) for (const c of rec.colliders) if ((c.x - x) ** 2 + (c.z - z) ** 2 < r * r) out.push({ kind: c.kind <= 4 ? ROCK_KINDS[c.kind] : c.kind === 5 ? 'log' : 'stump', x: c.x, y: c.y, z: c.z, collider: this.live.has(c.id) });
    return out;
  }
  chunkStats(): Array<{ key: string; rocks: number; plants: number; ms: number; counts: Record<string, number> }> {
    return [...this.chunks.entries()].map(([key, r]) => ({ key, rocks: r.rocks.length, plants: r.plants.reduce((a, im) => a + im.count, 0), ms: Math.round(r.ms), counts: r.counts }));
  }

  dispose(): void {
    for (const k of [...this.chunks.keys()]) this.release(k);
    this.rocks?.dispose(); this.rocks = null;
    this.worker?.terminate(); this.worker = null;
    for (const cb of this.waiting.values()) cb(null); this.waiting.clear();
  }
}

/** The collider trimesh with the full instance rotation + scale baked in (the physics pose is yaw-only). */
function bakedProxy(c: ColliderRec): ColliderProxy {
  const src = c.kind <= 4 ? rockColliderMesh(ROCK_KINDS[c.kind] as RockKind, c.variant) : deadwoodColliderMesh(c.kind === 5 ? 'log' : 'stump', c.variant);
  const m = new THREE.Matrix4().compose(new THREE.Vector3(), new THREE.Quaternion(c.q[0], c.q[1], c.q[2], c.q[3]), new THREE.Vector3(c.s[0], c.s[1], c.s[2]));
  const v = new THREE.Vector3(); const positions = new Array<number>(src.positions.length);
  for (let i = 0; i < src.positions.length; i += 3) { v.set(src.positions[i], src.positions[i + 1], src.positions[i + 2]).applyMatrix4(m); positions[i] = v.x; positions[i + 1] = v.y; positions[i + 2] = v.z; }
  return { shape: 'trimesh_static', positions, indices: src.indices };
}

